import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AccessibleModal } from '../components/AccessibleModal';
import { useCart } from '../hooks/useCart';
import { getCheckoutSummary, type CheckoutSummary, type CheckoutSummaryRequestItem } from '../server/api';

function formatMoney(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency.length === 3 ? currency.toUpperCase() : 'USD',
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${currency} ${amount}`;
  }
}

export default function CheckoutPage() {
  const { items } = useCart();
  const [summary, setSummary] = useState<CheckoutSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [payOpen, setPayOpen] = useState(false);

  const payload = useMemo((): CheckoutSummaryRequestItem[] => {
    return items.map((i) => ({
      type: i.type,
      bundle_id: i.type === 'bundle' ? i.bundle_id : undefined,
      asset_type: i.type === 'individual' ? i.asset_type : undefined,
      quantity: i.quantity,
    }));
  }, [items]);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (items.length === 0) {
        setSummary(null);
        setError(null);
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const res = await getCheckoutSummary(payload);
        if (cancelled) return;
        if (!res.ok) {
          setSummary(null);
          setError(res.error ?? 'Could not load checkout summary');
          setLoading(false);
          return;
        }
        setSummary(res.summary);
        setLoading(false);
      } catch {
        if (cancelled) return;
        setError('Request timed out — please try again');
        setLoading(false);
      }
    }
    void run();
    return () => {
      cancelled = true;
    };
  }, [items, payload]);

  const paymentLabel =
    summary?.payment_provider === 'paystack' ? 'Pay with Paystack →' : 'Pay with Stripe →';

  return (
    <div className="mx-auto max-w-3xl space-y-8 pb-10">
      <header>
        <h1 className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">Order Summary</h1>
        <p className="mt-1 text-sm text-muted sm:text-base">Review your order before payment</p>
      </header>

      {items.length === 0 ? (
        <div className="rounded-xl border border-border bg-surface/60 p-6 text-center text-sm text-muted">
          <p>Your cart is empty.</p>
          <Link to="/marketplace" className="mt-3 inline-block font-semibold text-accent hover:underline">
            Back to Marketplace
          </Link>
        </div>
      ) : null}

      {items.length > 0 && loading ? (
        <div className="space-y-3 animate-pulse" aria-busy="true">
          <div className="h-10 rounded-lg bg-foreground/10" />
          <div className="h-10 rounded-lg bg-foreground/10" />
          <div className="h-10 rounded-lg bg-foreground/10" />
        </div>
      ) : null}

      {error ? (
        <div
          className="rounded-xl border border-danger/30 bg-danger/5 p-6 text-center"
          role="alert"
        >
          <p className="text-sm text-danger">{error}</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-3 text-sm font-semibold text-accent hover:underline"
          >
            Try again
          </button>
        </div>
      ) : null}

      {summary && !loading ? (
        <>
          <div className="overflow-x-auto rounded-xl border border-border bg-surface/60">
            <table className="w-full min-w-[480px] text-left text-sm">
              <thead>
                <tr className="border-b border-border text-muted">
                  <th className="px-4 py-3 font-medium">Service</th>
                  <th className="px-4 py-3 font-medium">Qty</th>
                  <th className="px-4 py-3 font-medium">Unit Price</th>
                  <th className="px-4 py-3 font-medium">Total</th>
                </tr>
              </thead>
              <tbody className="text-foreground">
                {summary.items.map((line, idx) => (
                  <tr key={`${line.label}-${idx}`} className="border-b border-border/70">
                    <td className="px-4 py-3">{line.label}</td>
                    <td className="px-4 py-3 tabular-nums">{line.quantity}</td>
                    <td className="px-4 py-3 tabular-nums">
                      {formatMoney(line.unit_price, line.currency)}
                    </td>
                    <td className="px-4 py-3 font-medium tabular-nums">
                      {formatMoney(line.total_price, line.currency)}
                    </td>
                  </tr>
                ))}
                <tr className="bg-foreground/5 font-semibold">
                  <td className="px-4 py-3" colSpan={3}>
                    Subtotal
                  </td>
                  <td className="px-4 py-3 tabular-nums">{formatMoney(summary.subtotal, summary.currency)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="text-xs leading-relaxed text-muted">{summary.disclaimer}</p>
          <p className="text-xs leading-relaxed text-muted">
            Prices shown are in your local currency based on your location. Final charges may vary slightly due to
            exchange rates and applicable taxes. Your card will not be charged until you confirm payment on the next
            screen.
          </p>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <button
              type="button"
              onClick={() => setPayOpen(true)}
              className="inline-flex items-center justify-center rounded-lg bg-accent px-5 py-2.5 text-sm font-semibold text-cta-text transition hover:opacity-90"
            >
              {paymentLabel}
            </button>
            <Link to="/marketplace" className="text-sm font-semibold text-accent hover:underline">
              Back to Marketplace
            </Link>
          </div>
        </>
      ) : null}

      <AccessibleModal open={payOpen} onClose={() => setPayOpen(false)} title="Payment coming soon">
        <p className="text-sm text-muted">
          We&apos;re finalizing our payment setup. To get started now, contact us at{' '}
          <a href="mailto:hello@lognsec.com" className="font-semibold text-accent hover:underline">
            hello@lognsec.com
          </a>{' '}
          and we&apos;ll set you up manually.
        </p>
        <div className="mt-6 flex flex-col gap-2 sm:flex-row">
          <a
            href="mailto:hello@lognsec.com?subject=ShieldSME%20checkout"
            className="inline-flex flex-1 items-center justify-center rounded-lg border border-accent/50 bg-accent/10 px-4 py-2.5 text-sm font-semibold text-accent hover:bg-accent/20"
          >
            Email us →
          </a>
          <button
            type="button"
            onClick={() => setPayOpen(false)}
            className="inline-flex flex-1 items-center justify-center rounded-lg border border-border px-4 py-2.5 text-sm font-semibold text-foreground hover:bg-foreground/5"
          >
            Close
          </button>
        </div>
      </AccessibleModal>
    </div>
  );
}
